import { tx as translateInterfaceText } from '@/utils/i18n';
import { nanoid } from 'nanoid';
import { UploadItem, UploadStatus, UploadSummary } from '@/types/upload';
import { hierarchicalFoldersService } from './hierarchicalFoldersService';
import { useVideoDashboardStore } from '@/store/videoDashboard/store';
import { cacheThumbnail } from '@/utils/thumbnailCache';

export const MAX_CONCURRENT = 5;

type UploadListener = (items: UploadItem[]) => void;

// Generate video thumbnail from file - HD Quality (1280x720)
const generateVideoThumbnail = (file: File, videoId?: string): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.currentTime = 1;
    video.crossOrigin = 'anonymous';
    
    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      // HD Resolution: 1280x720
      canvas.width = 1280;
      canvas.height = 720;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // High quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(video, 0, 0, 1280, 720);
        // Quality 85% (premium)
        const thumbnail = canvas.toDataURL('image/jpeg', 0.85);
        URL.revokeObjectURL(url);
        
        // Cache the thumbnail for instant loading
        if (videoId) {
          cacheThumbnail(videoId, thumbnail);
        }
        
        resolve(thumbnail);
      } else {
        URL.revokeObjectURL(url);
        resolve('/placeholder.svg');
      }
    });
    
    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve('/placeholder.svg');
    });
  });
};

class UploadService {
  private items = new Map<string, UploadItem>();
  private listeners = new Set<UploadListener>();
  private activeUploads = 0;

  enqueue(files: File[], targetFolderId?: string | null): UploadItem[] {
    const newItems: UploadItem[] = files.map(file => ({
      id: nanoid(),
      file,
      name: file.name,
      size: file.size,
      status: 'queued' as UploadStatus,
      progress: 0,
      targetFolderId,
      thumbnail: '/placeholder.svg',
      abortController: new AbortController()
    }));

    newItems.forEach(item => this.items.set(item.id, item));
    this.notifyListeners();
    
    // Generate HD thumbnails asynchronously (non-blocking)
    newItems.forEach(async (item) => {
      const thumb = await generateVideoThumbnail(item.file, item.id);
      const existingItem = this.items.get(item.id);
      if (existingItem) {
        existingItem.thumbnail = thumb;
        this.notifyListeners();
      }
    });
    
    this.processQueue();

    return newItems;
  }

  pause(itemId: string): void {
    const item = this.items.get(itemId);
    if (item && item.status === 'uploading') {
      item.abortController?.abort();
      item.status = 'paused';
      this.activeUploads--;
      this.notifyListeners();
      this.processQueue();
    }
  }

  resume(itemId: string): void {
    const item = this.items.get(itemId);
    if (item && item.status === 'paused') {
      item.status = 'queued';
      item.progress = 0;
      item.abortController = new AbortController();
      this.notifyListeners();
      this.processQueue();
    }
  }

  cancel(itemId: string): void {
    const item = this.items.get(itemId);
    if (item) {
      if (item.status === 'uploading') {
        item.abortController?.abort();
        this.activeUploads--;
      }
      item.status = 'canceled';
      this.notifyListeners();
      this.processQueue();
    }
  }

  retry(itemId: string): void {
    const item = this.items.get(itemId);
    if (item && item.status === 'error') {
      item.status = 'queued';
      item.progress = 0;
      item.errorMessage = undefined;
      item.abortController = new AbortController();
      this.notifyListeners();
      this.processQueue();
    }
  }

  remove(itemId: string): void {
    this.items.delete(itemId);
    this.notifyListeners();
  }

  pauseAll(): void {
    Array.from(this.items.values())
      .filter(item => item.status === 'uploading' || item.status === 'queued')
      .forEach(item => this.pause(item.id));
  }

  resumeAll(): void {
    Array.from(this.items.values())
      .filter(item => item.status === 'paused')
      .forEach(item => this.resume(item.id));
  }

  cancelAll(): void {
    Array.from(this.items.values())
      .filter(item => ['queued', 'uploading', 'paused'].includes(item.status))
      .forEach(item => this.cancel(item.id));
  }

  clearCompleted(): void {
    const itemsToRemove = Array.from(this.items.values())
      .filter(item => ['done', 'error', 'canceled'].includes(item.status))
      .map(item => item.id);
    
    itemsToRemove.forEach(id => this.items.delete(id));
    
    if (itemsToRemove.length > 0) {
      this.notifyListeners();
    }
  }

  getSummary(): UploadSummary {
    const items = Array.from(this.items.values());
    return {
      total: items.length,
      queued: items.filter(i => i.status === 'queued').length,
      uploading: items.filter(i => i.status === 'uploading').length,
      paused: items.filter(i => i.status === 'paused').length,
      processing: items.filter(i => i.status === 'processing').length,
      done: items.filter(i => i.status === 'done').length,
      error: items.filter(i => i.status === 'error').length,
      canceled: items.filter(i => i.status === 'canceled').length
    };
  }

  getItems(): UploadItem[] {
    return Array.from(this.items.values());
  }

  subscribe(listener: UploadListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    const items = this.getItems();
    this.listeners.forEach(listener => listener(items));
  }

  private async processQueue(): Promise<void> {
    if (this.activeUploads >= MAX_CONCURRENT) return;

    const queuedItems = Array.from(this.items.values())
      .filter(item => item.status === 'queued');

    for (const item of queuedItems) {
      if (this.activeUploads >= MAX_CONCURRENT) break;

      this.activeUploads++;
      this.uploadItem(item);
    }
  }

  private async uploadItem(item: UploadItem): Promise<void> {
    item.status = 'uploading';
    item.progress = 0;
    (item as any)._uploadStartedAt = Date.now();
    this.notifyListeners();

    try {
      // Get userId from localStorage/sessionStorage
      const loginChecked = localStorage.getItem('checkedLogin');
      const storage = loginChecked ? localStorage : sessionStorage;
      const userStr = storage.getItem('user');

      if (!userStr) {
        throw new Error(translateInterfaceText("Usuário não autenticado"));
      }

      const user = JSON.parse(userStr);
      const userId = user.id_user || user.id;

      if (!userId) {
        throw new Error(translateInterfaceText("ID do usuário não encontrado"));
      }

      // Upload real com progresso via XHR (onUploadProgress do axios)
      const [uploadedVideo] = await hierarchicalFoldersService.uploadVideos(
        [item.file],
        userId,
        (pct: number) => {
          if (item.abortController?.signal.aborted) return;
          item.progress = pct;
          // Calcula velocidade e ETA aproximados
          const elapsedMs = Date.now() - (item as any)._uploadStartedAt;
          if (elapsedMs > 0 && pct > 0) {
            const uploadedBytes = (item.size * pct) / 100;
            item.speedBps = uploadedBytes / (elapsedMs / 1000);
            const remainingBytes = item.size - uploadedBytes;
            item.etaSec = item.speedBps > 0 ? remainingBytes / item.speedBps : 0;
          }
          this.notifyListeners();
        }
      );

      if (item.abortController?.signal.aborted) {
        return;
      }
      item.videoId = uploadedVideo.id;

      // Associate with folder if targetFolderId exists
      if (item.targetFolderId) {
        await hierarchicalFoldersService.addVideosToFolder(item.targetFolderId, [uploadedVideo.id]);
      }

      // Upload concluído — aguardando processamento no servidor
      item.progress = 100;
      item.status = 'processing';
      this.notifyListeners();

      // Adicionar vídeo à VideoDashboardStore com status 'processing'
      const videoForStore = {
        id: uploadedVideo.id,
        title: item.name.replace(/\.[^/.]+$/, ''),
        description: '',
        thumbnailUrl: item.thumbnail || '/placeholder.svg',
        videoUrl: URL.createObjectURL(item.file),
        uploadDate: new Date().toISOString(),
        duration: 0,
        views: 0,
        plays: 0,
        likes: 0,
        folderId: item.targetFolderId || null,
        status: 'processing' as const,
        tags: [],
        createdAt: new Date().toISOString(),
        isNew: true,
        isProcessing: true,
        viewCount: 0
      };

      useVideoDashboardStore.getState().addVideo(videoForStore);

      // Enviar notificação push quando o processamento concluir
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification('✅ Vídeo processado!', {
              body: `O vídeo "${item.name.replace(/\.[^/.]+$/, '')}" está pronto para uso.`,
              icon: '/pwa-192x192.png',
              badge: '/pwa-192x192.png',
              tag: `video-processed-${uploadedVideo.id}`,
            });
          } else {
            new Notification('✅ Vídeo processado!', {
              body: `O vídeo "${item.name.replace(/\.[^/.]+$/, '')}" está pronto para uso.`,
              icon: '/pwa-192x192.png',
            });
          }
        } catch (e) {
          console.warn('Notification error:', e);
        }
      }

      item.status = 'done';
      item.progress = 100;
      this.notifyListeners();

    } catch (error: any) {
      if (!item.abortController?.signal.aborted) {
        item.status = 'error';
        // Handle 403 permission errors specifically
        if (error?.response?.status === 403) {
          item.errorMessage = 'Você não tem permissão para fazer upload de vídeos. Contate o administrador.';
        } else {
          item.errorMessage = error instanceof Error ? error.message : 'Falha no upload';
        }
        this.notifyListeners();
      }
    } finally {
      this.activeUploads--;
      this.processQueue();
    }
  }

}

export const uploadService = new UploadService();
